import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, Plus, X } from "lucide-react";
import logoUrl from "@/assets/fightfund-logo.png";
import { usePwaInstall } from "@/hooks/usePwaInstall";

const DISMISS_KEY = "ff_pwa_dismissed_at";
const DISMISS_DAYS = 14;
const HIDDEN_PATHS = ["/embed", "/donation/confirmation"];

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPWA() {
  const location = useLocation();
  const { installable, iosSafari, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(recentlyDismissed());
  const [iosReady, setIosReady] = useState(false);

  useEffect(() => {
    if (!iosSafari) return undefined;
    const t = setTimeout(() => setIosReady(true), 2500);
    return () => clearTimeout(t);
  }, [iosSafari]);

  const onHiddenPath = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  const showIOS = iosSafari && iosReady;
  const isIOS = !installable && showIOS;
  const visible = !dismissed && !onHiddenPath && (installable || showIOS);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="pwa-install-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-md rounded-2xl border border-ff-border bg-white p-4 shadow-2xl shadow-ff-blue/10 sm:inset-x-auto sm:right-4"
        >
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-ff-medium transition-colors duration-200 hover:bg-[#F3F4F6] hover:text-ff-blue"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-ff-border bg-white">
              <img src={logoUrl} alt="FightFund" className="h-8 w-8 object-contain" />
            </span>
            <div className="min-w-0">
              <div className="font-heading text-sm font-bold text-ff-blue">Install FightFund</div>
              {isIOS ? (
                <p className="mt-0.5 text-xs leading-relaxed text-ff-medium">
                  Tap the{" "}
                  <Share className="inline h-3.5 w-3.5 -translate-y-0.5 text-ff-blue" /> Share icon,
                  then{" "}
                  <span className="font-semibold text-ff-blue">
                    Add to Home Screen <Plus className="inline h-3 w-3 -translate-y-0.5" />
                  </span>
                  .
                </p>
              ) : (
                <p className="mt-0.5 text-xs leading-relaxed text-ff-medium">
                  Add FightFund to your home screen for one-tap access to campaigns and updates.
                </p>
              )}
            </div>
          </div>

          {!isIOS && (
            <button
              data-testid="pwa-install-button"
              onClick={promptInstall}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-ff-red px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ff-red/90"
            >
              <Download className="h-4 w-4" /> Install app
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
